import type { InjectionKey, PropType } from 'vue';
import type { BlockDefinition, BlockPrivateRootContext, BlockRootContext, BlockSerialized } from '#layers/block-engine/types/block';
import type { BlockRootContextController } from '#layers/block-engine/utils/block/api/root-context';

export const blockRootContextApiFallbackKey = Symbol('rootContextApiFallback') as InjectionKey<BlockRootContextController>;
export const blockPrivateRootContextFallbackKey = Symbol('privateRootContextFallback') as InjectionKey<() => BlockPrivateRootContext>;

export interface ShouldRegisterBlockRootContextOptions {
	/**
	 * Explicit registration mode requested by the render caller.
	 */
	registerRootContext?: boolean | null;
	/**
	 * Parent root context API inherited from an ancestor render boundary.
	 */
	inheritedRootContextApi: BlockRootContextController | null;
}

/**
 * Returns whether the current render boundary should register as a root context source.
 */
export function shouldRegisterBlockRootContext(options: ShouldRegisterBlockRootContextOptions): boolean {
	if (options.inheritedRootContextApi) {
		return false;
	}

	return options.registerRootContext !== false;
}

export const blockRenderProps = {
	block: {
		type: [Object, String] as PropType<string | BlockSerialized | BlockDefinition>,
		required: true,
	},
	/**
	 * Props passed to the block component.
	 */
	props: Object,
	/**
	 * The spacing between blocks.
	 * Can be a CSS value like '8px', '1rem', etc.
	 */
	spacing: {
		type: String,
		default: '8px',
	},
	/**
	 * Don't wrap the blocks in error boundaries.
	 */
	skipErrorBoundary: {
		type: Boolean,
		default: false,
	},
	/**
	 * Don't wrap the block definition in a generated serialized block.
	 */
	unwrapBlockDefinition: {
		type: Boolean,
		default: false,
	},
	/**
	 * Override the root block id.
	 *
	 * Useful for example to generate the correct persisted storage keys when using a Block component with `<BlockRender />`.
	 */
	idOverride: {
		type: String,
		default: null,
	},
	context: Object as PropType<BlockRootContext>,
	privateContext: Object as PropType<BlockPrivateRootContext>,
	/**
	 * Register this render as a root context source.
	 *
	 * @internal
	 */
	registerRootContext: {
		type: Boolean as PropType<boolean | null>,
		default: null,
	},
};

export const blockRenderEmits = {
	'block:load': (_block: BlockDefinition | BlockSerialized) => true,
	'block:resolve': (_block: BlockDefinition | BlockSerialized) => true,
	'block:ref': (_val: any) => true,
};
